import { Typography, Chip, Tooltip, Box } from '@mui/material'
import { ShowMoreCell, TColumn } from './ColumnsByCountry'
import { TFunction } from 'i18next'
import { useSelector } from 'react-redux'

export const useBrColumnsKyb = (t: TFunction): TColumn[] => {
  const { currentCountry } = useSelector((state: any) => state.kybCountry)
  return [
    {
      field: 'fantasia',
      headerName: t('common:fantasyName'),
      headerAlign: 'center',
      minWidth: 120,
      maxWidth: 180,
      sortable: true,
      renderCell: ({ row }) => <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>{row.companyDetails?.fantasia || '---'}</Typography>,
    },
    {
      field: 'registrationStatus',
      headerName: t('common:registrationStatus'),
      headerAlign: 'center',
      minWidth: 140,
      maxWidth: 200,
      sortable: true,
      renderCell: ({ row }) => {
        return <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>{row.companyDetails?.situacao?.nome || '---'}</Typography>
      },
    },
    {
      field: 'capitalSocial',
      headerName: t('common:capitalSocial'),
      headerAlign: 'center',
      minWidth: 120,
      maxWidth: 180,
      sortable: true,
      renderCell: ({ row }) => <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>{row.companyDetails?.capitalSocial || '---'}</Typography>,
    },
    {
      field: 'legalOrganization',
      headerName: t('common:legalOrganization'),
      headerAlign: 'center',
      minWidth: 150,
      maxWidth: 220,
      sortable: true,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>{row.companyDetails?.naturezaJuridica?.descricao || '---'}</Typography>
      ),
    },
    {
      field: 'size',
      headerName: t('common:size'),
      headerAlign: 'center',
      minWidth: 100,
      maxWidth: 150,
      sortable: true,
      renderCell: ({ row }) => <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>{row.companyDetails?.porte?.descricao || '---'}</Typography>,
    },
    {
      field: 'phones',
      headerName: t('common:phones'),
      headerAlign: 'center',
      minWidth: 150,
      maxWidth: 220,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
            {Array.isArray(row.companyDetails?.telefones) && row.companyDetails.telefones.length > 0
              ? row.companyDetails.telefones
                  .filter((tel: { ddd?: string; numero?: string }) => tel.ddd && tel.numero)
                  .map((tel: { ddd?: string; numero?: string }) => (
                    <div key={`${tel.ddd}-${tel.numero}`}>{`(${tel.ddd}) ${tel.numero}`}</div>
                  ))
              : '---'}
          </Typography>
        )
      },
    },
    {
      field: 'simplesNacional',
      headerName: t('common:simplesNacional'),
      headerAlign: 'center',
      minWidth: 120,
      maxWidth: 180,
      sortable: true,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>{row.companyDetails?.simplesNacional?.optante ? 'Sim' : 'Não'}</Typography>
      ),
    },
    {
      field: 'cnae',
      headerName: t(`messages:kyb.companyCategoryMain.${currentCountry}`),
      headerAlign: 'center',
      minWidth: 350,
      maxWidth: 750,
      sortable: false,
      renderCell: ({ row }) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line', textAlign: 'center', width: '100%' }}>
              {row.companyDetails?.cnae?.descricao
                ? `${row.companyDetails.cnae.classe || row.companyDetails.cnae.subClasse || ''}: ${
                    row.companyDetails.cnae.descricao
                  }`
                : '---'}
            </Typography>
        </Box>
      ),
    },
    {
      field: 'cnaeSecondary',
      headerName: t(`messages:kyb.companyCategorySecondary.${currentCountry}`),
      headerAlign: 'center',
      minWidth: 350,
      maxWidth: 750,
      sortable: false,
      renderCell: ({ row }) => {
        const cnaeSecondary =
          Array.isArray(row.companyDetails?.cnae?.secundarias) && row.companyDetails.cnae.secundarias.length > 0
            ? row.companyDetails.cnae.secundarias
                .map(
                  (sec: { secao?: string; subClasse?: string; descricao?: string }) =>
                    `(${sec.secao || ''}) ${sec.subClasse || ''}: ${sec.descricao || ''}`,
                )
                .join(', ')
            : '---'
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            <ShowMoreCell text={cnaeSecondary} />
          </Box>
        )
      },
    },
    {
      field: 'monthlyQty',
      headerName: t('common:monthlyQty'),
      headerAlign: 'center',
      minWidth: 120,
      maxWidth: 180,
      sortable: true,
      renderCell: ({ row }) => <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>{row.monthlyQty || '---'}</Typography>,
    },
    {
      field: 'lawsuits',
      headerName: t('common:lawsuits'),
      headerAlign: 'center',
      minWidth: 250,
      maxWidth: 350,
      sortable: true,
      renderCell: ({ row }) => {
        const value = row.lawsuits;
        return (
          <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
            {value === 0 ? 0 : value || t('messages:kyb.without.lawsuits')}
          </Typography>
        );
      },
    },
    {
      field: 'tpv',
      headerName: t('common:tpv'),
      headerAlign: 'center',
      minWidth: 120,
      maxWidth: 180,
      sortable: true,
      renderCell: ({ row }) => <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>{row.tpv || '---'}</Typography>,
    },
    {
      field: 'isCurrentlyPEP',
      headerName: t('common:pepStatus'),
      headerAlign: 'center',
      minWidth: 250,
      maxWidth: 350,
      sortable: false,
      renderCell: ({ row }) => {
        if (row.kyc === null || row.kyc === undefined) {
          return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}><Chip size="small" style={{ backgroundColor: '#9e9e9e' }} label={t('common:runKycCheck')} /></Box>
        }
        const isCurrentlyPEP = row.kyc?.IsCurrentlyPEP
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <Chip
              size="small"
              style={{ backgroundColor: isCurrentlyPEP ? '#ff8a00' : '#00b85f' }}
              label={isCurrentlyPEP ? t('common:isCurrentlyPEPBUSINESS') : t('common:isNotPEPBUSINESS')}
            />
          </Box>
        )
      },
    },
    {
      field: 'sanctionsHistory',
      headerName: t('common:sanctionsStatus'),
      headerAlign: 'center',
      minWidth: 250,
      maxWidth: 350,
      sortable: false,
      renderCell: ({ row }) => {
        if (row.kyc === null || row.kyc === undefined) {
          return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}><Chip size="small" style={{ backgroundColor: '#9e9e9e' }} label={t('common:runKycCheck')} /></Box>
        }
        const hasSanctions = row.kyc?.SanctionsHistory && row.kyc.SanctionsHistory.length > 0
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <Chip
              size="small"
              style={{ backgroundColor: hasSanctions ? '#ff8a00' : '#00b85f' }}
              label={hasSanctions ? t('common:isCurrentlySanctioned') : t('common:isNotSanctioned')}
            />
          </Box>
        )
      },
    },
  ]
}

export const useBrColumnsKyc = (t: TFunction): TColumn[] => {
  return [
    {
      field: 'motherName',
      headerName: t('common:motherName'),
      headerAlign: 'center',
      minWidth: 200,
      maxWidth: 300,
      sortable: true,
      renderCell: ({ row }) => <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>{row.motherName ?? '---'}</Typography>,
    },
    {
      field: 'gender',
      headerName: t('common:gender'),
      headerAlign: 'center',
      minWidth: 100,
      maxWidth: 150,
      sortable: true,
      renderCell: ({ row }) => <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>{row.gender ?? '---'}</Typography>,
    },
    {
      field: 'rendaPresumida',
      headerName: t('common:rendaPresumida'),
      headerAlign: 'center',
      minWidth: 150,
      maxWidth: 200,
      sortable: true,
      renderCell: ({ row }) => <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>{row.rendaPresumida ?? '---'}</Typography>,
    },
    {
      field: 'situacaoImpostoRenda',
      headerName: t('common:situacaoImpostoRenda'),
      headerAlign: 'center',
      minWidth: 150,
      maxWidth: 200,
      sortable: true,
      renderCell: ({ row }) => <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>{row.situacaoImpostoRenda ?? '---'}</Typography>,
    },
    {
      field: 'cpfRegistrationDate',
      headerName: t('common:cpfRegistrationDate'),
      headerAlign: 'center',
      minWidth: 140,
      maxWidth: 180,
      sortable: true,
      renderCell: ({ row }) => {
        return <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>{row.cpfRegistrationDate || '---'}</Typography>
      },
    },
    {
      field: 'phoneNumber',
      headerName: t('common:phoneNumber'),
      headerAlign: 'center',
      minWidth: 150,
      maxWidth: 200,
      sortable: false,
      renderCell: ({ row }) => {
        if (row.phoneNumber) {
          return <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>{row.phoneNumber}</Typography>
        } else if (row.phoneNumberList?.length > 0) {
          return (
            <Tooltip title={row.phoneNumberList.map((p: any) => p.number).join(', ')}>
              <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>{row.phoneNumberList[0].number}</Typography>
            </Tooltip>
          )
        }
        return <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>---</Typography>
      },
    },
    {
      field: 'isCurrentlyPEP',
      headerName: t('common:pepStatus'),
      headerAlign: 'center',
      minWidth: 250,
      maxWidth: 350,
      sortable: false,
      renderCell: ({ row }) => {
        if (row.kyc === null || row.kyc === undefined) {
          return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}><Chip size="small" style={{ backgroundColor: '#9e9e9e' }} label={t('common:runKycCheck')} /></Box>
        }
        const isCurrentlyPEP = row.kyc?.IsCurrentlyPEP
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <Chip
              size="small"
              style={{ backgroundColor: isCurrentlyPEP ? '#ff8a00' : '#00b85f' }}
              label={isCurrentlyPEP ? t('common:isCurrentlyPEPBUSINESS') : t('common:isNotPEPBUSINESS')}
            />
          </Box>
        )
      },
    },
    {
      field: 'sanctionsHistory',
      headerName: t('common:sanctionsStatus'),
      headerAlign: 'center',
      minWidth: 250,
      maxWidth: 350,
      sortable: false,
      renderCell: ({ row }) => {
        if (row.kyc === null || row.kyc === undefined) {
          return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}><Chip size="small" style={{ backgroundColor: '#9e9e9e' }} label={t('common:runKycCheck')} /></Box>
        }
        const hasSanctions = row.kyc?.SanctionsHistory && row.kyc.SanctionsHistory.length > 0
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <Chip
              size="small"
              style={{ backgroundColor: hasSanctions ? '#ff8a00' : '#00b85f' }}
              label={hasSanctions ? t('common:isCurrentlySanctioned') : t('common:isNotSanctioned')}
            />
          </Box>
        )
      },
    },
  ]
}
